import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import Container from '../../layout/Container';
import SectionHeading from '../../common/SectionHeading';

const steps = [
  {
    number: '01',
    title: 'Assessment & Planning',
    description: 'Comprehensive evaluation of current infrastructure and future requirements.',
    points: [
      'IT environment analysis',
      'Business needs alignment',
      'Gap identification',
      'Future growth projection',
    ],
  },
  {
    number: '02',
    title: 'Solution Design',
    description: 'Crafting tailored infrastructure solutions based on assessment findings.',
    points: [
      'Architecture design',
      'Technology selection',
      'Vendor evaluation',
      'Implementation roadmap',
    ],
  },
  {
    number: '03',
    title: 'Procurement',
    description: 'Sourcing hardware and software components from trusted vendors.',
    points: [
      'Vendor management',
      'Cost optimization',
      'Quality assurance',
      'Delivery coordination',
    ],
  },
  {
    number: '04',
    title: 'Deployment & Integration',
    description: 'Implementing the new infrastructure with minimal disruption.',
    points: [
      'Installation services',
      'System configuration',
      'Data migration',
      'Integration with existing systems',
    ],
  },
];

const InfrastructurePlanning = () => {
  return (
    <section className="py-20 bg-white">
      <Container>
        <SectionHeading
          title="Building Efficient IT Infrastructure"
          subtitle="A systematic approach to planning, procurement, and deployment."
          center
        />
        
        <div className="mt-16">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`flex flex-col md:flex-row gap-8 ${
                index !== steps.length - 1 ? 'pb-12 mb-12 border-b border-gray-200' : ''
              }`}
            >
              <div className="md:w-64 flex-shrink-0">
                <div className="flex items-center">
                  <div className="text-5xl font-bold text-blue-600 mr-4">
                    {step.number}
                  </div>
                  <div className="w-24 h-1 bg-blue-200 hidden md:block"></div>
                </div>
                <h3 className="text-xl font-bold mt-4">{step.title}</h3>
              </div>
              
              <div className="flex-grow">
                <p className="text-gray-700 mb-6">
                  {step.description}
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {step.points.map((point, pointIndex) => (
                    <div 
                      key={pointIndex} 
                      className="flex items-center bg-gray-50 p-3 rounded-md"
                    >
                      <span className="text-green-600 mr-2">
                        <Check size={18} />
                      </span>
                      <span className="text-gray-800">{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
};

export default InfrastructurePlanning;